import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Landmark, Users, UserCog, Scale, Award, Wallet, FileCheck, ShieldAlert } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/governance-leadership`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Governance & Leadership | SEVAA Foundation";
const pageDescription =
  "Learn about SEVAA Foundation's governance and leadership structure, ethical decision-making, financial responsibility, legal compliance and accountability.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Governance",
    "Leadership",
    "Board and Trustees",
    "Legal Compliance",
    "Financial Responsibility",
    "NGO Accountability"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Governance & Leadership",
      item: pageUrl,
    },
  ],
};


const GovernanceLeadership = () => {
  // Data array for Governance Structure
  const structureData = [
    {
      id: 1,
      title: "Governing Board & Trustees",
      icon: <Users className="w-8 h-8 text-teal-600" />,
      content: "The organization is overseen by a dedicated Governing Board and Trustees responsible for long-term strategic direction, strict compliance, and overall oversight."
    },
    {
      id: 2,
      title: "Executive Team",
      icon: <UserCog className="w-8 h-8 text-orange-500" />,
      content: "Day-to-day operations are actively managed by our experienced Executive Team, strongly supported by our passionate staff and ground volunteers."
    },
    {
      id: 3,
      title: "Transparent Decision-Making",
      icon: <Scale className="w-8 h-8 text-teal-600" />,
      content: "All key decisions related to programs, finances, and partnerships are made collectively with utmost transparency and accountability."
    }
  ];

  // Data array for Ethical Governance Principles
  const ethicsData = [
    {
      id: 1,
      title: "Good Governance",
      icon: <Award className="w-6 h-6 text-teal-600" />,
      content: "Upholding the highest standards of integrity, leadership, and organizational management."
    },
    {
      id: 2,
      title: "Financial Responsibility",
      icon: <Wallet className="w-6 h-6 text-emerald-600" />,
      content: "Ensuring every donation is utilized effectively for maximum grassroots impact."
    },
    {
      id: 3,
      title: "Legal Compliance",
      icon: <FileCheck className="w-6 h-6 text-teal-600" />,
      content: "Strict adherence to all national laws, regulations, and NGO guidelines."
    },
    {
      id: 4,
      title: "Zero Tolerance",
      icon: <ShieldAlert className="w-6 h-6 text-rose-500" />,
      content: "Absolute zero tolerance for discrimination, exploitation, or any misuse of funds."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation governance and leadership structure" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation governance and leadership structure" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] bg-teal-900 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute -bottom-40 left-20 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-teal-800 rounded-2xl border border-teal-700 flex items-center justify-center mb-6 shadow-xl"
          >
            <Landmark className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
          >
            Governance & Leadership
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-teal-100 text-base md:text-lg max-w-2xl font-medium"
          >
            SEVAA is governed by a dedicated leadership team committed to ethical governance, strict compliance, and lasting social impact.
          </motion.p>
        </div>
      </div>

      {/* 2. CONTENT SECTION */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-8 md:p-12 overflow-hidden">
          
          {/* A. Governance Structure */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-teal-600"></span>
              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-wide">
                Governance Structure
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {structureData.map((item, index) => (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:shadow-lg hover:border-teal-100 transition-all group"
                >
                  <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-sm font-medium leading-relaxed">
                    {item.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <hr className="border-slate-100 mb-16" />

          {/* B. Ethical Governance */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-orange-500"></span>
              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-wide">
                Ethical Governance
              </h2>
            </div>

            <p className="text-slate-600 font-medium mb-8 leading-relaxed max-w-3xl">
              Trust is the foundation of our work. SEVAA strictly follows core principles to ensure that our operations are transparent, ethical, and fully focused on the communities we serve:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {ethicsData.map((ethics, index) => (
                <motion.div 
                  key={ethics.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-4 items-start p-6 bg-white border border-slate-100 rounded-2xl hover:shadow-md transition-shadow"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center border border-teal-100">
                    {ethics.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{ethics.title}</h3>
                    <p className="text-slate-600 text-sm font-medium leading-relaxed">
                      {ethics.content}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {/* Highlight Box for Zero Tolerance */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-rose-50 border-l-4 border-rose-500 p-6 rounded-r-2xl"
            >
              <p className="text-slate-800 font-bold text-sm leading-relaxed">
                SEVAA operates with a strict <span className="text-rose-600">Zero Tolerance Policy</span> regarding discrimination, exploitation, or the misuse of funds. We hold ourselves accountable to our donors, our partners, and the people we serve.
              </p>
            </motion.div>

          </div>

        </div>
      </div>

      </div>
    </>
  );
};

export default GovernanceLeadership;
